import { ref, computed } from 'vue';
import ApiClient from '@/ApiClient';
import { scopePayload } from '@/pages/recognition/constants';

const toOption = e => ({ text: `${e.full_name} - ${e.work_email}`, value: e.employee_id });

// Filters the nominator dropdown by the award's scope. Division membership is
// evaluated locally so it keeps working against an API that ignores the scope
// parameter; every other field is evaluated by the API.
export function useScopedEmployeeFiltering(scope) {
  const employees = ref([]);
  const divisionEmployeePairs = ref([]);
  const employeesLoading = ref(false);
  const divisionEmployeePairsLoading = ref(true);
  const eligibleEmployeeIds = ref(new Set());
  let latestRefresh = 0;

  const enabledScope = computed(() => scopePayload(scope.value).scope);
  const isScoped = computed(() => Object.keys(enabledScope.value).length > 0);

  const employeeOptions = computed(() => {
    const allEmployees = employees.value || [];
    if (!isScoped.value) {
      return allEmployees.map(toOption);
    }
    const allowed = eligibleEmployeeIds.value || new Set();
    return allEmployees.filter(e => allowed.has(e.employee_id)).map(toOption);
  });

  const allEmployeeOptions = computed(() => (employees.value || []).map(toOption));

  const divisionEligibleIds = () => {
    const ids = enabledScope.value.division || [];
    const pairs = divisionEmployeePairs.value || [];
    if (!ids.length || !pairs.length) {
      return new Set((employees.value || []).map(e => e.employee_id));
    }
    const selected = new Set(ids);
    const union = new Set();
    for (const { employee_id, division_id } of pairs) {
      if (selected.has(String(division_id))) {
        union.add(employee_id);
      }
    }
    return union;
  };

  const refreshEligibleEmployeeIds = async () => {
    const requestId = ++latestRefresh;
    let eligible = divisionEligibleIds();
    if (isScoped.value) {
      try {
        const rows = (await ApiClient.listRecognitionEmployees({ scope: enabledScope.value }))?.data || [];
        const inScope = new Set(rows.map(e => e.employee_id));
        eligible = new Set([...eligible].filter(id => inScope.has(id)));
      } catch {
        // Division filtering above still applies.
      }
    }
    if (requestId === latestRefresh) {
      eligibleEmployeeIds.value = eligible;
    }
  };

  const loadEmployeesAndDivisionPairs = () => [
    (async () => {
      try {
        employeesLoading.value = true;
        employees.value = (await ApiClient.listRecognitionEmployees())?.data || [];
      } catch {
        // silently ignore
      } finally {
        employeesLoading.value = false;
      }
    })(),
    (async () => {
      try {
        divisionEmployeePairs.value = (await ApiClient.getEmployeeDivisionInfo())?.data || [];
      } catch {
        // silently ignore — approver filtering falls back to all employees
      } finally {
        divisionEmployeePairsLoading.value = false;
      }
    })(),
  ];

  return {
    employees,
    divisionEmployeePairs,
    employeesLoading,
    divisionEmployeePairsLoading,
    eligibleEmployeeIds,
    employeeOptions,
    allEmployeeOptions,
    isScoped,
    refreshEligibleEmployeeIds,
    loadEmployeesAndDivisionPairs,
  };
}
